import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useFeatureFlag, useFeatureFlagWithLoading } from '../hooks/useFeatureFlag';
import { teamApiService } from '../services/teamApiService';
import { BackgroundSaveService } from '../services/backgroundSaveService';
import { debugError } from '../utils/debug';
import { ProTrialEndedModal } from './ProTrialEndedModal';
import { ProTrialDowngradeConfirmModal } from './ProTrialDowngradeConfirmModal';
import { ProUpgradeModal } from './ProUpgradeModal';
import { ELEVATED_MODAL_Z_INDEX } from './Modal';

type GateMode = 'ended' | 'upgrade' | 'confirm-downgrade';

/**
 * ProTrial: the app-global owner of the "your Pro trial ended" flow.
 *
 * A lapsed trial leaves the team on Pro until a human decides, so the next member to log in is
 * shown a blocking choice: upgrade (the normal self-upgrade flow) or downgrade the whole team.
 *
 * Self-clearing — every resolution path clears the trial dates server-side AND force-logs-out the
 * team, so the next session reads fresh values and this renders nothing.
 */
export const ProTrialGate: React.FC = () => {
    // useFeatureFlagWithLoading, not useFeatureFlag: the case-count fetch below runs in an effect,
    // and a plain flag read would latch false while the flags are still loading.
    const { enabled: proTrialEnabled, ready: flagsReady } = useFeatureFlagWithLoading('ProTrial');
    // Only gates whether we can honestly quote "N cases will lock" -- the lock itself is a
    // separate feature and may be off.
    const proDowngradeEnabled = useFeatureFlag('ProDowngrade');

    const { user, isAuthenticated, logout } = useAuthContext();
    const { addAlert } = useAlert();

    const [mode, setMode] = useState<GateMode>('ended');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCountLoading, setIsCountLoading] = useState(false);
    const [totalCases, setTotalCases] = useState<number | null>(null);
    const [casesWithProData, setCasesWithProData] = useState<number | null>(null);
    const [unknownCases, setUnknownCases] = useState<number | null>(null);
    // Set when the case-count fetch itself fails (network error, 500, etc). Distinct from "not
    // fetched yet" -- unlike that state, this must keep the Confirm button disabled (see
    // ProTrialDowngradeConfirmModal), because "we don't know" is not the same as "zero cases."
    const [countLoadFailed, setCountLoadFailed] = useState(false);
    // Bumped to force a retry after a failed fetch, since the effect below otherwise only runs
    // once per mode-entry (guarded on totalCases still being null).
    const [countRetryToken, setCountRetryToken] = useState(0);

    const trialEndsAt = user?.team?.trialEndsAt;
    const trialEnded = !!trialEndsAt && new Date(trialEndsAt) <= new Date();
    const active = flagsReady && proTrialEnabled && isAuthenticated && trialEnded;

    // Fetch the impact numbers only when the user actually asks to downgrade -- most users take
    // the upgrade path, and this saves a query per login for them.
    useEffect(() => {
        if (!active || mode !== 'confirm-downgrade' || !proDowngradeEnabled) return;
        if (totalCases !== null) return;

        let cancelled = false;
        setIsCountLoading(true);
        setCountLoadFailed(false);
        teamApiService.getProDataCaseCount()
            .then(result => {
                if (cancelled) return;
                setTotalCases(result.totalCases);
                setCasesWithProData(result.casesWithProData);
                setUnknownCases(result.unknownCases);
            })
            .catch(err => {
                if (cancelled) return;
                // NOT non-fatal: without a count we cannot honestly tell the user what will lock,
                // so the modal must keep the confirm button disabled and offer a retry rather than
                // silently letting the downgrade proceed blind.
                debugError('ProTrialGate: failed to load Pro-data case count', err);
                setCountLoadFailed(true);
            })
            .finally(() => { if (!cancelled) setIsCountLoading(false); });

        return () => { cancelled = true; };
    }, [active, mode, proDowngradeEnabled, totalCases, countRetryToken]);

    const retryCountLoad = useCallback(() => {
        setCountLoadFailed(false);
        setCountRetryToken(t => t + 1);
    }, []);

    const handleConfirmDowngrade = useCallback(async () => {
        setIsSubmitting(true);
        try {
            // ProTrialGate is mounted at the app root and can trigger on ANY route, not only at
            // login -- the periodic auth revalidation (~every 5 min) can surface it mid-session,
            // including while the user has a case open in the wizard. BackgroundSaveService.flush()
            // is a plain singleton (not tied to SimpleCaseContext, so it's safe to call even when
            // no case is mounted here -- it's a no-op if nothing is registered) that drains any
            // page-local debounce (e.g. BudgetPage's/NetWorthPage's own timers) and the autosave
            // engine's own debounce, saving in-progress work to the server before the downgrade
            // forces everyone out. Without this, edits sitting in either debounce would be lost
            // with no warning beyond the confirmation's generic "everyone will be signed out" line.
            await BackgroundSaveService.flush();

            const result = await teamApiService.downgradeAfterTrial();
            if (!result.success) {
                throw new Error(result.message || 'Failed to downgrade');
            }
            // The server force-logged-out the whole team, so this session's token is already
            // stale. Log out directly rather than waiting for the next request to 401.
            await logout();
        } catch (err) {
            debugError('ProTrialGate: downgrade failed', err);
            addAlert('error', 'We could not downgrade your team. Please try again.');
            setIsSubmitting(false);
        }
    }, [logout, addAlert]);

    if (!active) return null;

    return (
        <>
            <ProTrialEndedModal
                isOpen={mode === 'ended'}
                onUpgradeClick={() => setMode('upgrade')}
                onDowngradeClick={() => setMode('confirm-downgrade')}
            />

            {/* The existing self-upgrade flow, unchanged -- same usage as Navigation.tsx. Elevated
                z-index to stay above an ordinary case-level modal, same as the mandatory modal
                that opened it (see Modal.ELEVATED_MODAL_Z_INDEX). */}
            <ProUpgradeModal
                isOpen={mode === 'upgrade'}
                onClose={() => setMode('ended')}
                featureDescription=""
                generic
                zIndex={ELEVATED_MODAL_Z_INDEX}
            />

            <ProTrialDowngradeConfirmModal
                isOpen={mode === 'confirm-downgrade'}
                onCancel={() => setMode('ended')}
                onConfirm={handleConfirmDowngrade}
                isSubmitting={isSubmitting}
                isCountLoading={isCountLoading}
                totalCases={totalCases}
                casesWithProData={casesWithProData}
                unknownCases={unknownCases}
                countLoadFailed={countLoadFailed}
                onRetryCountLoad={retryCountLoad}
                showCaseLockWarning={proDowngradeEnabled}
            />
        </>
    );
};
